// PDF-form (Documents & E-Signatures) API actions for the GHL embed.
//
// Routes live under `/ghl/embed/pdf-forms/*` behind the `ghl.sso` middleware;
// the company + location are derived from the verified SSO context, never from
// user-supplied IDs. The flow is opportunity-driven: pick an opportunity, the
// backend resolves its linked project, and forms are listed/created/sent for
// that project. See GhlEmbedPdfFormController (MONGOOSE-FR-009).
import { Api } from 'Utils/api';

const getHeaders = (ssoToken: string) => ({ 'X-GHL-SSO': ssoToken });

// ---- types ------------------------------------------------------------------

export interface OpportunityOption {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  stageName: string | null;
}

export interface PdfFormTemplate {
  id: number;
  name: string;
  status: string;
  hasSignature: boolean;
}

export interface PdfFormSubmission {
  id: number;
  uuid: string;
  templateName: string | null;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  status: string;
  prefilledUrl: string | null;
  signedUrl: string | null;
  signedAt: string | null;
  emailedAt: string | null;
  smsSentAt: string | null;
  createdAt: string | null;
}

export interface Prefill {
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  projectId: number | null;
}

// ---- mappers ----------------------------------------------------------------

const buildContactName = (c: Record<string, any> | null | undefined): string | null => {
  if (!c) return null;
  const display = (c.display_name || '').trim();
  if (display) return display;
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
};

const mapOpportunity = (o: Record<string, any>): OpportunityOption => ({
  id: String(o.id),
  name: o.name || 'Untitled',
  contactName: buildContactName(o.contact),
  contactEmail: o.contact?.email ?? null,
  contactPhone: o.contact?.phone ?? null,
  stageName: o.stage?.name ?? null,
});

const mapTemplate = (t: Record<string, any>): PdfFormTemplate => ({
  id: Number(t.id),
  name: t.name,
  status: t.status,
  hasSignature: Boolean(t.has_signature),
});

const mapSubmission = (s: Record<string, any>): PdfFormSubmission => ({
  id: Number(s.id),
  uuid: s.uuid,
  templateName: s.template?.name ?? null,
  clientName: s.client_name,
  clientEmail: s.client_email ?? null,
  clientPhone: s.client_phone ?? null,
  status: s.status,
  prefilledUrl: s.prefilled_url ?? null,
  signedUrl: s.signed_url ?? null,
  signedAt: s.signed_at ?? null,
  emailedAt: s.emailed_at ?? null,
  smsSentAt: s.sms_sent_at ?? null,
  createdAt: s.created_at ?? null,
});

// ---- requests ---------------------------------------------------------------

export const searchOpportunities = async (
  ssoToken: string,
  search: string
): Promise<OpportunityOption[]> => {
  const params: Record<string, string | number> = {
    limit: 20,
    include: 'contact,stage',
  };
  const trimmed = search.trim();
  if (trimmed) params['filter[search]'] = trimmed;

  const { data } = await Api.get('/ghl/embed/opportunities', {
    params,
    headers: getHeaders(ssoToken),
  });
  return (data?.data || []).map(mapOpportunity);
};

export const listTemplates = async (ssoToken: string): Promise<PdfFormTemplate[]> => {
  const { data } = await Api.get('/ghl/embed/pdf-forms/templates', {
    headers: getHeaders(ssoToken),
  });
  return (data?.data || []).map(mapTemplate);
};

export const getPrefill = async (
  ssoToken: string,
  opportunityId: string
): Promise<Prefill> => {
  const { data } = await Api.get(`/ghl/embed/pdf-forms/${opportunityId}/prefill`, {
    headers: getHeaders(ssoToken),
  });
  return {
    clientName: data?.client_name ?? '',
    clientEmail: data?.client_email ?? null,
    clientPhone: data?.client_phone ?? null,
    projectId: data?.project_id ?? null,
  };
};

export const listSubmissions = async (
  ssoToken: string,
  opportunityId: string
): Promise<PdfFormSubmission[]> => {
  const { data } = await Api.get(`/ghl/embed/pdf-forms/${opportunityId}/submissions`, {
    headers: getHeaders(ssoToken),
  });
  return (data?.data || []).map(mapSubmission);
};

export interface CreateSubmissionPayload {
  pdfFormTemplateId: number;
  clientName: string;
  clientEmail?: string | null;
  clientPhone?: string | null;
}

export const createSubmission = async (
  ssoToken: string,
  opportunityId: string,
  payload: CreateSubmissionPayload
): Promise<PdfFormSubmission> => {
  const { data } = await Api.post(
    `/ghl/embed/pdf-forms/${opportunityId}/submissions`,
    {
      pdf_form_template_id: payload.pdfFormTemplateId,
      client_name: payload.clientName,
      client_email: payload.clientEmail || null,
      client_phone: payload.clientPhone || null,
    },
    { headers: getHeaders(ssoToken) }
  );
  return mapSubmission(data?.data || {});
};

export const sendForSignature = async (
  ssoToken: string,
  submissionId: number,
  channels: { email?: string | null; phone?: string | null }
): Promise<string> => {
  const { data } = await Api.post(
    `/ghl/embed/pdf-forms/submissions/${submissionId}/send`,
    { email: channels.email || null, phone: channels.phone || null },
    { headers: getHeaders(ssoToken) }
  );
  return data?.message || 'Sent successfully.';
};
